import { useNavigate } from 'react-router-dom'
import { Pencil, Mail, Phone, Building2, Calendar } from 'lucide-react'
import { useProfile } from '@/hooks/useAuth'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'

// 状态配置
const statusConfig: Record<number, { label: string; variant: 'default' | 'destructive' }> = {
	1: { label: '正常', variant: 'default' },
	0: { label: '禁用', variant: 'destructive' },
}

export function ProfilePage() {
	const navigate = useNavigate()
	const { data: user, isLoading } = useProfile()

	if (isLoading) {
		return (
			<div className="mx-auto max-w-2xl space-y-6">
				<Skeleton className="h-48 w-full rounded-lg" />
				<Skeleton className="h-64 w-full rounded-lg" />
			</div>
		)
	}

	if (!user) return null

	const statusInfo = statusConfig[user.status] || { label: '未知', variant: 'secondary' as const }

	return (
		<div className="mx-auto max-w-2xl space-y-6">
			{/* 页面标题 */}
			<div>
				<h1 className="text-2xl font-bold tracking-tight">个人中心</h1>
				<p className="text-muted-foreground">查看您的个人信息</p>
			</div>

			{/* 头像和基本信息 */}
			<Card>
				<CardContent className="flex flex-col items-center gap-4 pt-6">
					<Avatar className="h-24 w-24">
						<AvatarImage src={user.avatar} alt={user.username} />
						<AvatarFallback className="text-2xl bg-primary text-primary-foreground">
							{user.username?.charAt(0).toUpperCase() || 'U'}
						</AvatarFallback>
					</Avatar>
					<div className="text-center">
						<h2 className="text-xl font-semibold">{user.nickname || user.username}</h2>
						<p className="text-sm text-muted-foreground">@{user.username}</p>
					</div>
					<div className="flex items-center gap-2">
						<Badge>{user.is_super_admin ? '超级管理员' : '管理员'}</Badge>
						<Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
					</div>
				</CardContent>
			</Card>

			{/* 详细信息 */}
			<Card>
				<CardContent className="space-y-4 pt-6">
					<h3 className="font-semibold text-lg">详细信息</h3>
					<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
						<div className="flex items-center gap-3 rounded-lg border p-3">
							<Mail className="h-4 w-4 text-muted-foreground" />
							<div>
								<p className="text-xs text-muted-foreground">邮箱</p>
								<p className="text-sm">{user.email || '-'}</p>
							</div>
						</div>
						<div className="flex items-center gap-3 rounded-lg border p-3">
							<Phone className="h-4 w-4 text-muted-foreground" />
							<div>
								<p className="text-xs text-muted-foreground">手机号</p>
								<p className="text-sm">{user.phone || '-'}</p>
							</div>
						</div>
						<div className="flex items-center gap-3 rounded-lg border p-3">
							<Building2 className="h-4 w-4 text-muted-foreground" />
							<div>
								<p className="text-xs text-muted-foreground">部门</p>
								<p className="text-sm">{user.department_id ? `部门 #${user.department_id}` : '-'}</p>
							</div>
						</div>
						<div className="flex items-center gap-3 rounded-lg border p-3">
							<Calendar className="h-4 w-4 text-muted-foreground" />
							<div>
								<p className="text-xs text-muted-foreground">注册时间</p>
								<p className="text-sm">{user.created_at ? new Date(user.created_at).toLocaleDateString('zh-CN') : '-'}</p>
							</div>
						</div>
					</div>
				</CardContent>
			</Card>

			{/* 操作按钮 */}
			<div className="flex justify-center">
				<Button onClick={() => navigate('/settings/account')}>
					<Pencil className="mr-2 h-4 w-4" />
					编辑资料
				</Button>
			</div>
		</div>
	)
}
